import { Download, FileText } from 'lucide-react';

import { urlDeArquivo } from '@/api/http-client';
import { getExamesControllerLaudoEspecialPdfUrl } from '@/api/generated/exames/exames';
import type { TipoDeLaudoEspecialResponse } from '@/api/generated/model';
import { Button } from '@/shared/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog';

interface Props {
  exameId: string;
  tipo: TipoDeLaudoEspecialResponse | null;
  aoFechar: () => void;
}

/**
 * Pré-visualização do laudo especial, com download.
 *
 * ⚠️ Não reusa o `VisualizadorDeDocumento` das discussões, apesar de parecerem
 * a mesma coisa. Lá a toolbar do PDF é escondida e o menu de contexto bloqueado
 * DE PROPÓSITO — é o controle que impede o advisor de salvar o exame de outro
 * médico. Aqui o laudo é do paciente de quem está olhando, e baixar é o objetivo.
 * Emprestar aquele componente e furar o controle com uma prop enfraqueceria os
 * dois casos.
 *
 * ⚠️ A URL é montada com `urlDeArquivo`, não chamada pelo cliente gerado: o
 * `httpClient` faz `.text()` + `JSON.parse` incondicionais e quebraria no
 * binário (o próprio `http-client.ts` documenta esta saída). Mesmo caminho do
 * PDF de receituário e de requisição. Sendo same-origin, o cookie de sessão vai
 * junto — inclusive no `<iframe>`.
 */
export function LaudoEspecialDialogo({ exameId, tipo, aoFechar }: Props) {
  const url = tipo
    ? urlDeArquivo(getExamesControllerLaudoEspecialPdfUrl(exameId, { tipo: tipo.codigo }))
    : null;

  return (
    <Dialog open={tipo !== null} onOpenChange={(aberto) => !aberto && aoFechar()}>
      <DialogContent className="max-w-4xl h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-8">
            <FileText className="w-4 h-4 text-brand shrink-0" />
            Laudo de {tipo?.rotulo}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 rounded-md bg-muted/30 overflow-hidden">
          {url && (
            <iframe
              // `key` na url: trocar de tipo com o dialogo aberto tem de
              // recarregar o PDF, e o Chrome não refaz o fetch só com src novo.
              key={url}
              src={url}
              title={`Laudo de ${tipo?.rotulo}`}
              className="w-full h-full border-0"
            />
          )}
        </div>

        <div className="flex justify-end gap-2 shrink-0">
          {url && (
            <Button asChild variant="outline">
              {/* `download` só é respeitado em same-origin — e é o caso. */}
              <a href={url} download>
                <Download className="w-4 h-4" />
                Baixar PDF
              </a>
            </Button>
          )}
          <Button variant="ghost" onClick={aoFechar}>
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
